import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiFetch, mediaUrl } from "./utils/api";
import { formatDate } from "./utils/time";
import { useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import BackButton from "./components/BackButton";
import Footer from "./components/Footer";
import { SkeletonArticle } from "./components/ui";
import NotAvailable from "./components/NotAvailable";
import "./Content.css";

/**
 * The full update.
 *
 * Hits /updates/:id, which re-runs the audience filter SERVER-SIDE. The list page is
 * not a security boundary — anyone can type /update/7 into the address bar — so the
 * server decides again, per row, and 404s anything outside this viewer's audience.
 */
const UpdateDetails = () => {
    const { id } = useParams();
    const { isAuthenticated } = useAuth();

    const [update, setUpdate] = useState(null);
    const [status, setStatus] = useState("loading"); // loading | ready | notfound

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setStatus("loading");
            try {
                const res = await apiFetch(`/api/Dolluzcorp/updates/${id}`);
                const data = await res.json();
                if (cancelled) return;

                if (res.ok && data.success) {
                    setUpdate(data.data);
                    setStatus("ready");
                } else {
                    setStatus("notfound");
                }
            } catch {
                if (!cancelled) setStatus("notfound");
            }
        })();

        return () => { cancelled = true; };
        // Signing in may bring this update into view.
    }, [id, isAuthenticated]);

    /**
     * One rendered height shared by every image in this update.
     *
     * images_max_height is a HEIGHT, despite the column name — it is set as `height`, not
     * `max-height`, so the author's slider works in BOTH directions. As a max-height it did
     * nothing to any image already shorter than the value, so dragging it appeared dead.
     * (The column keeps the old name to avoid a migration.)
     *
     * object-fit: contain is what keeps that honest: if max-width has to narrow the box,
     * the image letterboxes inside it at its true aspect ratio instead of stretching.
     * undefined → natural size, exactly as before.
     */
    const imgCap =
        Number(update?.images_max_height) > 0
            ? {
                height: `${Number(update.images_max_height)}px`,
                width: "auto",
                maxWidth: "100%",
                objectFit: "contain",
            }
            : undefined;

    return (
        <>
            <a className="skip-link" href="#main">Skip to content</a>
            <Header />

            <main id="main" className="article">
                <div className="page-wrap">
                    <BackButton fallback="/updates" />

                    {status === "loading" && <SkeletonArticle />}

                    {status === "notfound" && (
                        <NotAvailable kind="update" backTo="/updates" backLabel="Browse updates" />
                    )}

                    {status === "ready" && update && (
                        <article>
                            <header className="article-head">
                                <span className="chip chip-update">Update</span>
                                <h1 className="article-title">{update.title}</h1>
                                {update.subject && <p className="article-standfirst">{update.subject}</p>}
                                <div className="article-meta">
                                    <time dateTime={update.updated_time || update.created_time}>
                                        {formatDate(update.updated_time || update.created_time)}
                                    </time>
                                </div>
                            </header>

                            {/* Images lead the article, above the body — this matches dAdmin's
                                full-page preview, so what the author approved is what gets
                                published. Multiple images keep their existing grid layout. */}
                            {update.images?.length > 0 && (
                                <div className="article-figures">
                                    {update.images.map((path, i) => (
                                        <img
                                            key={i}
                                            src={mediaUrl(path)}
                                            alt={`Attachment ${i + 1} for ${update.title}`}
                                            loading="lazy"
                                            /* images_max_height is a SINGLE rendered height for
                                               ALL of this update's images; null → natural size. */
                                            style={imgCap}
                                        />
                                    ))}
                                </div>
                            )}

                            <div
                                className="prose article-body"
                                dangerouslySetInnerHTML={{ __html: update.description }}
                            />
                        </article>
                    )}
                </div>
            </main>

            <Footer />
        </>
    );
};

export default UpdateDetails;
